/** Uma mensagem na conversa: texto, anexos, cartão, reações, lido e o estado do envio. */
import { memo, useEffect, useRef, useState } from "react";

import { horaCurta, marcaDeEnvio, podeNotificarDeNovo, salvarPreferenciasDoChat } from "../../services/chat";
import { CartaoVivo } from "./CartaoVivo";
import { EmojiApple, SeletorDeEmoji, aplicarTomDePele, comTomDePele, tomDePele, useEmojisProntos } from "./Emojis";
import { Avatar, ICONE, Ic } from "./Icones";
import { MarcaDeEnvio } from "./Marcas";
import { Anexos } from "./Midia";
import { TextoComEmojis } from "./TextoComEmojis";

const REACOES_RAPIDAS = ["👍", "✅", "👀", "🚨", "😂", "🙏"];
/** Folga para o mouse sair da mensagem e voltar sem o seletor de reação sumir. */
const SAIDA_DO_MOUSE_MS = 350;

/** Hora e marca no canto de baixo à direita, DENTRO do balão — como no WhatsApp. */
function Rodape({ m }) {
  return (
    <span className="pchat-meta">
      {m.editada_em && <span className="editada">editada</span>}
      <span className="hora">{horaCurta(m.ocorrida_em || m.criada_em)}</span>
      {m.minha && !m.apagada && <MarcaDeEnvio marca={marcaDeEnvio(m)} />}
    </span>
  );
}

/**
 * O menu "⋯" flutua por cima da conversa, preso ao botão: não empurra a
 * mensagem nem o layout (antes entrava no fluxo, embaixo do balão). Abre
 * para cima na metade de baixo da tela e fecha com clique fora ou ESC.
 */
function MaisAcoes({ m, acoes, aoFechar, ancoraRef, praCima }) {
  const menuRef = useRef(null);
  const fazer = (fn) => () => { aoFechar(); fn(m); };
  useEffect(() => {
    const aoClicar = (e) => {
      if (menuRef.current?.contains(e.target) || ancoraRef.current?.contains(e.target)) return;
      aoFechar();
    };
    const aoTeclar = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault(); // é deste menu: a conversa não fecha junto
      aoFechar();
    };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("touchstart", aoClicar);
    window.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("touchstart", aoClicar);
      window.removeEventListener("keydown", aoTeclar);
    };
  }, [aoFechar, ancoraRef]);
  return (
    <div ref={menuRef} className={`pchat-mais ${m.minha ? "a-esquerda" : "a-direita"} ${praCima ? "pra-cima" : "pra-baixo"}`} role="menu">
      {m.texto && <button type="button" role="menuitem" onClick={fazer(acoes.copiar)}>Copiar</button>}
      <button type="button" role="menuitem" onClick={fazer(acoes.encaminhar)}>Encaminhar</button>
      <button type="button" role="menuitem" onClick={fazer(acoes.fixar)}>{m.fixada ? "Soltar do topo" : "Fixar no topo"}</button>
      <button type="button" role="menuitem" onClick={fazer(acoes.virarTarefa)}>Virar tarefa</button>
      {podeNotificarDeNovo(m) && <button type="button" role="menuitem" onClick={fazer(acoes.notificarDeNovo)}>Notificar novamente</button>}
    </div>
  );
}

/**
 * Seletor rápido de reação: flutua preso ao botão (não empurra nada nem é
 * cortado pela conversa — abre para cima na metade de baixo da tela) e fecha
 * com clique fora ou ESC; tirar o mouse da mensagem também fecha (o Balao cuida).
 */
function SeletorRapido({ m, acoes, praCima, ancoraRef, aoFechar, guardarTom }) {
  const caixaRef = useRef(null);
  const [completo, setCompleto] = useState(false);
  useEffect(() => {
    const aoClicar = (e) => {
      if (caixaRef.current?.contains(e.target) || ancoraRef.current?.contains(e.target)) return;
      aoFechar();
    };
    const aoTeclar = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault(); // é deste seletor: a conversa não fecha junto
      aoFechar();
    };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("touchstart", aoClicar);
    window.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("touchstart", aoClicar);
      window.removeEventListener("keydown", aoTeclar);
    };
  }, [aoFechar, ancoraRef]);
  const reagir = (e) => { acoes.reagir(m, e); aoFechar(); };
  // 👍 e 🙏 no tom que a pessoa escolheu no seletor (antes ficavam sempre amarelos).
  useEmojisProntos();
  const tom = tomDePele();
  return (
    <div ref={caixaRef} className={`pchat-reagir ${m.minha ? "a-esquerda" : "a-direita"} ${praCima ? "pra-cima" : "pra-baixo"}`} role="menu" aria-label="Reagir à mensagem">
      {REACOES_RAPIDAS.map((base) => comTomDePele(base, tom)).map((e) => (
        <button key={e} type="button" role="menuitem" aria-label={`Reagir com ${e}`} onClick={() => reagir(e)}><EmojiApple native={e} size="1.35em" /></button>
      ))}
      <button type="button" className="mais-emojis" aria-label="Mais emojis" title="Mais emojis" onClick={() => setCompleto(true)}><Ic d={ICONE.plus} size={14} /></button>
      {completo && <SeletorDeEmoji alinhar={m.minha ? "direita" : "esquerda"} aoEscolherTom={guardarTom} aoFechar={() => setCompleto(false)} aoEscolher={reagir} />}
    </div>
  );
}

/** As ações ficam na LINHA do balão: com reação embaixo, não descem junto (antes alinhavam ao fim do bloco). */
function AcoesDaMensagem({ m, podeEditar, acoes, mais, maisRef, alternarMais, fecharMais, reagindo, reagirRef, alternarReagir, fecharReagir, guardarTom }) {
  return (
    <div className={`pchat-acoes ${mais || reagindo ? "aberto" : ""}`}>
      <button type="button" title="Responder" aria-label="Responder" onClick={() => acoes.responder(m)}><Ic d={ICONE.undo} size={14} /></button>
      <span className="pchat-mais-ancora" ref={reagirRef}>
        <button type="button" title="Reagir" aria-label="Reagir" aria-haspopup="menu" aria-expanded={!!reagindo} onClick={alternarReagir}><Ic d={ICONE.smile} size={15} /></button>
        {reagindo && <SeletorRapido m={m} acoes={acoes} ancoraRef={reagirRef} praCima={reagindo === "cima"} aoFechar={fecharReagir} guardarTom={guardarTom} />}
      </span>
      {m.minha && podeEditar && (
        <>
          <button type="button" title="Editar" aria-label="Editar" onClick={() => acoes.editar(m)}><Ic d={ICONE.edit} size={14} /></button>
          <button type="button" title="Apagar" aria-label="Apagar" onClick={() => acoes.apagar(m)}><Ic d={ICONE.trash} size={14} /></button>
        </>
      )}
      <span className="pchat-mais-ancora" ref={maisRef}>
        <button type="button" title="Mais ações" aria-label="Mais ações" aria-haspopup="menu" aria-expanded={!!mais} onClick={alternarMais}><Ic d={ICONE.mais} size={14} /></button>
        {mais && <MaisAcoes m={m} acoes={acoes} ancoraRef={maisRef} praCima={mais === "cima"} aoFechar={fecharMais} />}
      </span>
    </div>
  );
}

/** Tocar nisto faz a coisa dele (abrir anexo, tocar áudio, reagir), não mostra as ações. */
const ALVO_PROPRIO_DO_TOQUE = "a, button, audio, video, input, textarea, select, [role='button'], [role='menu']";

function Balao({ m, podeEditar, acoes, mostrarAutor, request, versao, destacada = false, fotoDoAutor = null }) {
  const [mais, setMais] = useState(null); // null = fechado; "cima" | "baixo" = aberto
  const maisRef = useRef(null);
  const [reagindo, setReagindo] = useState(null); // idem, para o seletor de reação
  const reagirRef = useRef(null);
  const saidaRef = useRef(null);
  const ladoParaAbrir = (ref) => {
    const rect = ref.current?.getBoundingClientRect();
    return rect && rect.top > window.innerHeight / 2 ? "cima" : "baixo";
  };
  const alternarMais = () => setMais((atual) => (atual ? null : ladoParaAbrir(maisRef)));
  const alternarReagir = () => setReagindo((atual) => (atual ? null : ladoParaAbrir(reagirRef)));
  // Tirou o mouse da mensagem: o seletor de reação some (com folga para o mouse voltar).
  const aoSairDaMensagem = () => {
    if (!reagindo) return;
    clearTimeout(saidaRef.current);
    saidaRef.current = setTimeout(() => setReagindo(null), SAIDA_DO_MOUSE_MS);
  };
  const aoVoltarNaMensagem = () => clearTimeout(saidaRef.current);
  useEffect(() => () => clearTimeout(saidaRef.current), []);
  // No toque (sem hover) as ações não ficam ao lado de cada balão — eram três
  // botões por mensagem apertando o texto. Tocar na mensagem mostra as dela;
  // tocar fora esconde. Com mouse segue o hover (CSS). Igual ao chat do Cut.
  const linhaRef = useRef(null);
  const [acoesNoToque, setAcoesNoToque] = useState(false);
  useEffect(() => {
    if (!acoesNoToque) return undefined;
    const fora = (e) => {
      if (!linhaRef.current?.contains(e.target)) setAcoesNoToque(false);
    };
    document.addEventListener("touchstart", fora);
    document.addEventListener("mousedown", fora);
    return () => {
      document.removeEventListener("touchstart", fora);
      document.removeEventListener("mousedown", fora);
    };
  }, [acoesNoToque]);
  const aoTocarNaLinha = (e) => {
    if (!window.matchMedia?.("(hover: none)").matches) return;
    if (e.target.closest(ALVO_PROPRIO_DO_TOQUE)) return;
    setAcoesNoToque((v) => !v);
  };
  const guardarTom = (tom) => {
    aplicarTomDePele(tom);
    salvarPreferenciasDoChat(request, { tom_de_pele: tom }).catch(() => undefined);
  };
  const sistema = m.autor_tipo === "sistema";
  const pendente = m.pendente;
  const emGrupo = m.lido_por?.length > 0 && !m.lido_por_todos;
  return (
    <div
      id={`msg-${m.id}`}
      className={`pchat-msg ${m.minha ? "minha" : ""} ${m.cartao ? "com-cartao" : ""} ${m.reacoes?.length > 0 ? "com-reacao" : ""} ${pendente ? `pendente ${pendente}` : ""} ${destacada ? "destacada" : ""}`}
      onMouseLeave={aoSairDaMensagem}
      onMouseEnter={aoVoltarNaMensagem}
    >
      {!m.minha && <Avatar nome={m.autor_nome} bot={sistema} foto={fotoDoAutor} />}
      <div className="corpo">
        {mostrarAutor && (
          <div className="quem">
            <b>{m.minha ? "Você" : m.autor_nome}</b>
            {m.autor_tipo === "operador" && <span>· operador</span>}
          </div>
        )}
        <div ref={linhaRef} className={`pchat-linha${acoesNoToque ? " mostra-acoes" : ""}`} onClick={aoTocarNaLinha}>
        <div className={`pchat-balao ${sistema ? "sistema" : ""} ${m.apagada ? "apagada" : ""} ${m.menciona_me ? "me-cita" : ""}`}>
          {(m.encaminhada || m.fixada) && (
            <div className="pchat-rotulos">
              {m.encaminhada && <span>↪ encaminhada</span>}
              {m.fixada && <span>📌 fixada</span>}
            </div>
          )}
          {m.resposta_a && <div className="pchat-resposta"><b>{m.resposta_a.autor_nome}</b>{m.resposta_a.texto ? <TextoComEmojis texto={m.resposta_a.texto} mencoes={false} /> : "anexo"}</div>}
          {m.apagada ? "Mensagem apagada" : m.texto ? <TextoComEmojis texto={m.texto} /> : null}
          {!m.apagada && m.cartao && <CartaoVivo cartao={m.cartao} request={request} versao={versao} />}
          {!m.apagada && <Anexos anexos={m.anexos} minha={m.minha} aoAbrir={(i) => acoes.abrir(m.anexos, i)} aoOuvir={(a) => acoes.ouvirAudio?.(m, a)} />}
          <Rodape m={m} />
          {m.reacoes?.length > 0 && (
            <div className="pchat-reacoes na-bolha">
              {m.reacoes.map((r) => <button key={r.emoji} type="button" className={r.minha ? "minha" : ""} title={r.minha ? "Tirar a sua reação" : "Reagir também"} onClick={() => acoes.reagir(m, r.emoji)}><EmojiApple native={r.emoji} /> {r.total > 1 ? r.total : ""}</button>)}
            </div>
          )}
        </div>
        {!sistema && !m.apagada && !pendente && (
          <AcoesDaMensagem
            m={m} podeEditar={podeEditar} acoes={acoes} mais={mais} maisRef={maisRef} alternarMais={alternarMais} fecharMais={() => setMais(null)}
            reagindo={reagindo} reagirRef={reagirRef} alternarReagir={alternarReagir} fecharReagir={() => setReagindo(null)} guardarTom={guardarTom}
          />
        )}
        </div>
        {m.minha && emGrupo && (
          <div className="pchat-lido" title={m.lido_por.map((l) => `${l.nome} · ${horaCurta(l.em)}`).join("\n")}>
            lido por {m.lido_por.length === 1 ? m.lido_por[0].nome : `${m.lido_por.length} pessoas`}
          </div>
        )}
        {pendente === "enviando" && <div className="pchat-envio">enviando…{m.progresso > 0 && m.progresso < 100 ? ` ${m.progresso}%` : ""}</div>}
        {pendente === "falhou" && (
          <div className="pchat-envio falhou" role="alert">
            Não enviada · <button type="button" onClick={() => acoes.tentarDeNovo(m)}>Tentar de novo</button> · <button type="button" onClick={() => acoes.descartar(m)}>Descartar</button>
          </div>
        )}
      </div>
    </div>
  );
}


// Só redesenha quando a mensagem (ou o que ela mostra) muda: "digitando" e
// mensagem nova não redesenham mais a conversa inteira, balão por balão.
const BalaoMemorizado = memo(Balao);
export { BalaoMemorizado as Balao };
